import { useEffect, useState } from 'react';
import { assetQuery, sanityFetch } from '../services/sanity';
import { breakdownDuration, zeroPad } from '../utils/date';
import { capitalizeFirstLetter } from '../utils/string';
import { agencyQuery } from './agencies';
import { awardQuery } from './awards';
import { craftQuery } from './crafts';
import { nationQuery } from './nations';
// ---------- FUNCTIONS ----------
export const getAstronaut = async (config, id) => {
    return sanityFetch(config, `*[
    _type == 'astronaut'
    && _id == '${id}'
  ][0]${astronautQuery}`);
};
export const getAstronautBySlug = async (config, slug) => {
    return sanityFetch(config, `*[
    _type == 'astronaut'
    && slug.current == '${slug}'
  ][0]${astronautQuery}`);
};
export const useAstronaut = (config, slug, _astronaut, isFocused = true) => {
    const [astronaut, setAstronaut] = useState(_astronaut);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (!astronaut) {
            setLoading(true);
        }
    }, [astronaut]);
    useEffect(() => {
        if (!slug)
            return;
        getAstronautBySlug(config, slug).then((v) => {
            setAstronaut(v);
            setLoading(false);
        });
    }, [isFocused, slug]);
    return { astronaut, loading };
};
export const deriveAstronautOccupationInfo = (astronaut) => {
    const branchBlank = !astronaut.militaryBranch || astronaut.militaryBranch === '';
    const rankBlank = !astronaut.rank || astronaut.rank === '';
    const isMilitary = (!rankBlank && astronaut.rank !== 'Civilian') ||
        (!branchBlank && astronaut.militaryBranch !== 'Civilian');
    const occupationBlank = !astronaut.occupation || astronaut.occupation.length === 0;
    const branch = astronaut.militaryBranch || '';
    const rank = capitalizeFirstLetter(astronaut.rank || '');
    const militaryOccupation = rank === branch ? rank : [branch, rank].join(' ');
    const rankMissing = isMilitary && rankBlank;
    const branchMissing = isMilitary && branchBlank;
    const occupationMissing = !isMilitary && occupationBlank;
    const militaryOverview = rankMissing && branchMissing ? 'Occupation Unknown' : militaryOccupation;
    const civilianOverview = occupationMissing
        ? 'Occupation Unknown'
        : astronaut.occupation;
    const overview = isMilitary ? militaryOverview : civilianOverview;
    const astroPageDisplay = overview?.replace('Occupation Unknown', '');
    const dataMissing = isMilitary
        ? rankMissing || branchMissing
        : occupationMissing;
    return {
        overview: overview || '',
        astroPageDisplay: astroPageDisplay || '',
        occupationMissing: dataMissing,
        isMilitary
    };
};
export const deriveAstronautMillisInSpace = (astronaut) => {
    if (!astronaut || astronaut.totalMinutesInSpace === undefined) {
        return undefined;
    }
    let millisInSpace = (astronaut.totalMinutesInSpace || 0) * 60 * 1000;
    if (astronaut.lastActiveLaunchDate) {
        const nowTimestamp = Date.parse(new Date().toISOString());
        const dateTimestamp = Date.parse(new Date(astronaut.lastActiveLaunchDate.utc).toISOString());
        millisInSpace += nowTimestamp - dateTimestamp;
    }
    return millisInSpace;
};
export const deriveAstronautMillisSpacewalking = (astronaut) => {
    if (!astronaut || astronaut.totalSecondsSpacewalking === undefined) {
        return undefined;
    }
    let millisSpacewalking = (astronaut.totalSecondsSpacewalking || 0) * 1000;
    if (astronaut.lastSpacewalkStartTime) {
        const nowTimestamp = Date.parse(new Date().toISOString());
        const dateTimestamp = Date.parse(new Date(astronaut.lastSpacewalkStartTime.utc).toISOString());
        millisSpacewalking += nowTimestamp - dateTimestamp;
    }
    return millisSpacewalking;
};
export const deriveAstronautIsAnimal = (astronaut) => {
    return astronaut?.speciesGroup !== 'human';
};
export const deriveAstronautIsRobot = (astronaut) => {
    return astronaut?.speciesGroup === 'robots';
};
export const getHumanCrewmates = (astronauts, astronaut) => {
    if (!astronauts || astronauts.length === 0 || !astronaut) {
        return;
    }
    const missions = (astronauts || []).find((a) => a._id === astronaut._id)?.missions || [];
    const crewmateIds = [];
    missions.forEach((mission) => {
        (mission?.astronauts || []).forEach((a) => {
            if (!crewmateIds.includes(a._id)) {
                crewmateIds.push(a._id);
            }
        });
    });
    const crewmates = astronauts.filter((a) => crewmateIds.includes(a._id));
    return crewmates.filter((a) => a._id !== astronaut._id);
};
export const getNonHumanCrewmates = (astronauts, astronaut) => {
    if (!astronauts || astronauts.length === 0 || !astronaut) {
        return;
    }
    const missions = (astronauts || []).find((a) => a._id === astronaut._id)?.missions || [];
    const crewmateIds = [];
    missions.forEach((mission) => {
        (mission?.animalCrew || []).forEach((a) => {
            if (!crewmateIds.includes(a._id)) {
                crewmateIds.push(a._id);
            }
        });
    });
    const crewmates = astronauts.filter((a) => crewmateIds.includes(a._id));
    return crewmates.filter((a) => a._id !== astronaut._id);
};
export const deriveAstronautTimeInSpace = (astronaut) => {
    let totalDuration = (astronaut.totalMinutesInSpace || 0) * 60 * 1000;
    const ongoingSince = astronaut.lastActiveLaunchDate
        ? astronaut.lastActiveLaunchDate.utc
        : new Date().toISOString();
    if (ongoingSince) {
        const nowTimestamp = Date.parse(new Date().toISOString());
        const dateTimestamp = Date.parse(ongoingSince);
        totalDuration += nowTimestamp - dateTimestamp;
    }
    const { dayString, hourString, minuteString } = breakdownDuration(totalDuration);
    return `${dayString}:${hourString}:${minuteString}`;
};
export const deriveAstronautTimeSpacewalking = (astronaut) => {
    const { totalSecondsSpacewalking } = astronaut;
    if (totalSecondsSpacewalking === undefined) {
        return 'Unknown';
    }
    const t = totalSecondsSpacewalking * 1000;
    const seconds = Math.floor((t / 1000) % 60);
    const minutes = Math.floor((t / 1000 / 60) % 60);
    const hours = Math.floor((t / (1000 * 60 * 60)) % 24);
    const days = Math.floor(t / (1000 * 60 * 60 * 24));
    return `${zeroPad(days, 2)}:${zeroPad(hours, 2)}:${zeroPad(minutes, 2)}`;
};
// ---------- QUERIES ----------
export const astronautQuery = `{
  _id,
  aid,
  slug,
  suffix,
  name,
  firstName,
  middleName,
  lastName,
  gender,
  speciesGroup,
  species,
  rank,
  militaryBranch,
  occupation,
  birthdate,
  deathdate,
  totalMinutesInSpace,
  overview,
  overviewSourceUrl,
  description,
  astroNumber,
  overallNumber,
  inSpace,
  spacewalking,
  lastLaunchDate,
  lastActiveLaunchDate,
  missionCount,
  spacewalkCount,
  totalSecondsSpacewalking,

  image ${assetQuery},
  otherImages[] ${assetQuery},
  nations[]->${nationQuery},
  agencies[]->${agencyQuery},
  awards[]${awardQuery},

  "nationsNames": nations[]->[].name,
  "spacewalks": *[_type=='spacewalk' && references(^._id)]{
    _id,
    name,
    astronauts,
    startDate,
    start,
    end
  },
  "missions": *[_type=='mission' && references(^._id)]{
    _id,
    slug,
    name,
    seriesName,
    seriesNumber,
    shortDescription,
    vagueLaunchDate,
    astronauts,
    image ${assetQuery},
    spacecrafts[]->${craftQuery}
  }
}`;
//# sourceMappingURL=astronauts.js.map